import { Permission, Permission_index } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
// eslint-disable-next-line @nx/enforce-module-boundaries
import jwtDecode from 'jwt-decode';
import { getTokenStorage } from './storage/TokenStorage';
import { AuthStorage, type PortalUser } from './types/auth.types';
import type { DecodedPortalToken } from './types/token.types';
import { CoreACLs } from './types/weave.types';

export const LOGIN_ERROR_CAUSE = {
  credentials: 'credentials',
  unknown: 'unknown',
  webauthn: 'webauthn credential issue',
  ipRestricted: 'unauthorized IP address',
};

export const clearStoredAuthData = () => {
  getTokenStorage().clearStoredAuthData();
  localStorage.removeItem(AuthStorage.user);
};

export const setUser = (user: PortalUser) => {
  localStorage.setItem(AuthStorage.user, JSON.stringify(user));
};

/** updates the local user -- used when the user updates their name/email */
export const updateUser = (updatedFields: Partial<Omit<PortalUser, 'userId'>>) => {
  const currentUser = getUser();
  if (currentUser) {
    const updatedUser = { ...currentUser, ...updatedFields };
    setUser(updatedUser);
    return updatedUser;
  }
  return undefined;
};

export const getUser = (): PortalUser | undefined => {
  const data = localStorage.getItem(AuthStorage.user);
  if (!data) return undefined;
  try {
    return JSON.parse(data) as PortalUser;
  } catch {
    return undefined;
  }
};

export const getWeaveToken = () => {
  return getTokenStorage().getToken();
};

export const getJTI = () => {
  return getTokenStorage().getJTI();
};

export const setWeaveToken = (token: string, jti: string) => {
  getTokenStorage().setToken(token, jti);
};

export const clearCache = () => {
  getTokenStorage().clearCache();
};

/** @deprecated Use `clearCache` instead. */
export const clearInMemoryCache_testOnly = clearCache;

export const onWeaveTokenChange = (cb: (token: string | undefined, jtiToken: string | undefined) => void) => {
  return getTokenStorage().onTokenChange(cb);
};

/** @deprecated The weave JWT will eventually go away, so don't depend on this. Ask users-infra if you have a use case that can't be solved without it. */
export const getDecodedWeaveToken = <A extends CoreACLs | Permission_index = CoreACLs>() => {
  const storage = getTokenStorage();
  const cached = storage.getDecodedToken<A>();
  if (cached && !isTimeExpiredRelative(cached.exp, cached.iat)) {
    return cached;
  }
  // Invalidate decoded cache so getDecodedToken re-reads the raw token (which may have changed via another tab)
  storage.invalidateDecodedToken();
  return storage.getDecodedToken<A>();
};

export const getDecodedPortalToken = (token: string) => {
  if (token) {
    try {
      const decodedToken = jwtDecode<DecodedPortalToken>(token);
      return decodedToken;
    } catch (e) {
      return;
    }
  }
  return;
};

/**
 * Returns the time when the token was received, in milliseconds. If null, uses the provided fallback value.
 * @param fallback - Fallback value to use if the token_received_at is not found in localStorage. Typically this is the iat value of the token in milliseconds.
 */
function getTokenReceivedAt(fallback: number) {
  return getTokenStorage().getTokenReceivedAt(fallback);
}

/**
 * The weave token determines if the user is authorized to make calls to the weave API
 */
export const isWeaveTokenActive = () => {
  const token = getDecodedWeaveToken();
  if (token) {
    return !isTimeExpiredRelative(token.exp, token.iat);
  }
  return false;
};

export const isWeaveTokenBufferActive = (): boolean => {
  const token = getDecodedWeaveToken();
  if (token) {
    return !isTimeExpiredRelative(token.expBuffer, token.iat);
  }
  return false;
};

/**
 * Whether the weave token was received (i.e. the user authenticated) within the last `maxAgeMs` milliseconds.
 *
 * `token_received_at` is stamped with the local system clock at the moment the token was received and is compared
 * against the current system clock, so the elapsed-time comparison is immune to absolute clock skew (some customers
 * have wildly inaccurate system clocks). This mirrors the relative-time approach used by `isTimeExpiredRelative`.
 *
 * Fails safe: returns false when no receipt timestamp is stored.
 */
export const wasWeaveTokenReceivedWithin = (maxAgeMs: number): boolean => {
  const receivedAt = getTokenReceivedAt(-1);
  if (receivedAt < 0) return false;
  return Date.now() - receivedAt <= maxAgeMs;
};

// Simple non-secure hash function that can be used to generate a hash from a string
export const hashCode = function (input: string): number {
  let hash = 0;
  if (input.length === 0) return hash;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
};

type LoginData = Record<
  string,
  {
    latestLocationId: string;
    latestLocationIds: string[];
    latestOrganizationId: string;
    latestSelectedScope?: { scopeType: string; scopeId: string } | null;
  }
>;
export const LOGIN_DATA_KEY = 'weave.login-data';

export const getLoginData = (userId: string) => {
  const data = localStorageHelper.get<LoginData>(LOGIN_DATA_KEY);
  if (!data) {
    return undefined;
  } else {
    return { ...data?.['*'], ...data?.[userId] };
  }
};

export const setLoginData = (userId: string, data: Partial<LoginData[keyof LoginData]>) => {
  const prev = localStorageHelper.get<LoginData>(LOGIN_DATA_KEY) ?? {};
  const newData = { ...prev, [userId]: { ...prev?.[userId], ...data } };
  return localStorageHelper.create(LOGIN_DATA_KEY, newData);
};

export const clearLoginData = () => {
  localStorageHelper.delete(LOGIN_DATA_KEY);
};

export const locationRedirect = (user?: PortalUser) => {
  const targetRedirectLocation = localStorage.getItem(LOCATION_REDIRECT_STORAGE_KEY);
  if (targetRedirectLocation) {
    const userID = user?.userID ?? '*';
    console.info(`Setting location id to ${targetRedirectLocation} from url for user: ${userID}`);
    setLoginData(userID, {
      latestLocationId: targetRedirectLocation,
      latestLocationIds: [targetRedirectLocation],
      latestOrganizationId: undefined,
    });
    if (userID !== '*') {
      localStorage.removeItem(LOCATION_REDIRECT_STORAGE_KEY);
    }
  }
};

export const LOCATION_REDIRECT_STORAGE_KEY = 'targetRedirectLocation';

const getLocationACLs = <A extends CoreACLs | Permission_index = CoreACLs>(locationId: string) =>
  getDecodedWeaveToken<A>()?.ACLS[locationId];
const getWeaveACLs = <A extends CoreACLs | Permission_index = CoreACLs>() => getLocationACLs<A>('weave');
const isWeaveUserInternal = () => getDecodedWeaveToken()?.type === 'weave';

/**
 * @discouraged Use `hasSchemaACL` instead.
 * Instead of checking isWeaveUser, you should perform an ACL check.
 * For example, if you have a feature like team-chat, you can require access through an ACL.
 * If you need a new ACL for the feature, please reach out to the #user-infra-support channel.
 * This approach ensures that all features require ACL access—even for Weave users.
 */
export const isWeaveUser = () => {
  return isWeaveUserInternal();
};
/** @deprecated Use `hasSchemaACL` in combination with the `Permission` enum from `schema-gen-ts` instead. */
export const hasACL = <A extends CoreACLs | Permission_index = CoreACLs>(locationId: string, acl: A | A[]) => {
  const ACLs = Array.isArray(acl) ? acl : [acl];
  const availableACLs: A[] | undefined = isWeaveUserInternal() ? getWeaveACLs<A>() : getLocationACLs<A>(locationId);
  return ACLs?.every((aclItem) => availableACLs?.includes(aclItem));
};

export const hasSchemaACL = (locationId: string, acl: Permission | Permission[]) => {
  const ACLs = Array.isArray(acl) ? acl : [acl];
  const aclIndexes = ACLs.map((aclItem) => Permission_index[aclItem]);
  return hasACL<Permission_index>(locationId, aclIndexes);
};

export const checkAggregateACL = (
  locationIds: string[],
  acl: Permission | Permission[],
  aggregationStrategy: 'all' | 'any' = 'all'
) => {
  const locationACLs = locationIds.map((locationId) => ({ locationId, hasACL: hasSchemaACL(locationId, acl) }));
  const locationACLsMap = Object.fromEntries(locationACLs.map((l) => [l.locationId, l.hasACL]));

  return {
    aclValuesByLocationId: locationACLsMap,
    aggregateValue:
      aggregationStrategy === 'all' ? locationACLs.every((l) => l.hasACL) : locationACLs.some((l) => l.hasACL),
    hasMixedValues: locationACLs.some((l) => l.hasACL) && locationACLs.some((l) => !l.hasACL),
  };
};

type FilterLocationIdsByACLsArgs = {
  locationIds: string[];
  acls: Permission | Permission[];
  strategy?: 'all' | 'any';
};

/**
 * Filters an array of location IDs based on whether they have the specified ACL permissions.
 *
 * @param locationIds - Array of location IDs to filter
 * @param acls - Permission or array of permissions to check
 * @param strategy (Optional) - Filtering strategy:
 *   - 'all': locationId must have ALL specified permissions (default)
 *   - 'any': locationId must have AT LEAST ONE of the specified permissions
 * @returns A filtered array of location IDs that match the ACL requirements
 */
export const filterLocationIdsByACLs = ({ locationIds, acls, strategy = 'all' }: FilterLocationIdsByACLsArgs) => {
  const aclArr = Array.isArray(acls) ? acls : [acls];
  return locationIds.filter((locationId) =>
    strategy === 'all'
      ? aclArr.every((acl) => hasSchemaACL(locationId, acl))
      : aclArr.some((acl) => hasSchemaACL(locationId, acl))
  );
};

// These are all the public routes, scraped from routeTree.gen.ts
// TODO find a more scalable way to do this
export const AUTH_CHECK_IGNORE_ROUTES = [
  '/accept-invite',
  '/front-channel-logout',
  '/hydra-error',
  '/insurance-eligibility-report',
  '/it-partner-quotes',
  '/login',
  '/nr-public-test',
  '/okta-redirect',
  '/onboarding-demo',
  '/password-reset',
  '/portal/accept-invite',
  '/portal/login',
  '/portal/user-invite',
  '/porting',
  '/porting/loa',
  '/quotes',
  '/reset',
  '/reset-password',
  '/sign-in',
  '/sign-in/callback',
  '/test',
  '/user-invite',
];
const defaultPage = '/home/dashboard';
const isDisallowedLastVisitedPage = (uri: string): boolean => {
  return (
    AUTH_CHECK_IGNORE_ROUTES.some((ignoreRoute) => uri.includes(ignoreRoute)) ||
    // Older code blocked /portal prefixed routes. Keeping this since there might be some edge cases that rely on
    // this behavior (e.g. legacy links)
    uri.startsWith('/portal') ||
    // The old code blocked routes that had "/" prefix which seemed to match everything since all routes start
    // with "/". I added an explicit check for the root route instead.
    uri === '/' ||
    uri.startsWith('/?ssoTeam=') ||
    // The old code blocked any routes that started with /home/dashboard however, since this is the default route, it
    // would cause redirect links to settings routes to also be blocked since those routes can also start
    // with /home/dashboard (e.g. /home/dashboard/#settings/phone/overrides). Added an explicit check for the
    // root /home/dashboard route instead.
    uri === '/home/dashboard'
  );
};

// TODO: support multiple auth methods and tabs (think embedding multiple views on a page each with their own auth flow happening)
export const setLastVisitedPage = (uri: string) => {
  if (isDisallowedLastVisitedPage(uri)) {
    clearLastVisitedPage();
    return;
  }
  localStorageHelper.create(AuthStorage.original_uri, uri);
};

export const getLastVisitedPage = (appDefaultURL?: string) => {
  const page = localStorageHelper.get(AuthStorage.original_uri);
  if (page && !isDisallowedLastVisitedPage(page)) {
    return page;
  }
  return appDefaultURL || defaultPage;
};

export const clearLastVisitedPage = () => {
  localStorageHelper.delete(AuthStorage.original_uri);
};

export const clearLocalStorageKeys = (keysToClear: Array<string>) => {
  if (keysToClear.length) {
    keysToClear.forEach((key) => {
      localStorageHelper.delete(key);
    });
  }
};

export const decodeWeaveToken = jwtDecode;

/* ~~~~~~~~~~~~~~~~~~~ local storage ~~~~~~~~~~~~~~~~~~~ */
export const localStorageHelper = {
  get: <T = string>(key: string): T | undefined => {
    const data = localStorage.getItem(key);
    if (!data) return undefined;
    try {
      return JSON.parse(data) as T;
    } catch (e) {
      return data as T;
    }
  },
  create: (key: string, value: string | Record<string, any>) => {
    if (typeof value === 'object') {
      localStorage.setItem(key, JSON.stringify(value));
      return;
    }
    localStorage.setItem(key, value);
  },
  delete: (key: string | string[]) => {
    if (typeof key === 'string') {
      localStorage.removeItem(key);
      return;
    }
    key.forEach((k) => {
      localStorage.removeItem(k);
    });
  },
  deleteWithPrefix: (prefix: string) => {
    if (!prefix) return;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);

      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  },
};

// This function checks if the token is expired based on the time it was received and computes the expiration time relative
// to the system time, which means we don't have to worry about system clock accuracy.
export function isTimeExpiredRelative(
  // exp and iat are in seconds
  exp: string | number | undefined,
  iat: string | number | undefined
): boolean {
  if (exp == null || iat == null) return false;
  const expNum = Number(exp);
  const iatNum = Number(iat);
  // edge case handling - not NaN, Infinity, or -Infinity
  if (!Number.isFinite(expNum) || !Number.isFinite(iatNum)) return false;

  const lifespan = (expNum - iatNum) * 1000; // convert to milliseconds
  const tokenReceivedAt = getTokenReceivedAt(iatNum * 1000);
  const expTime = tokenReceivedAt + lifespan;

  // Date.now() can be wildly inaccurate in absolute terms but this comparison is safe because
  // we calculated the expiration time dynamically based on the lifespan of the token and the system time the token was received.
  return expTime <= Date.now();
}

// this checks if the token is expired based on the system time and assumes the system time is accurate
export const isTimeExpired = (exp?: string | number | undefined): boolean => {
  if (!exp) return false;
  if (typeof exp === 'string') exp = parseInt(exp);
  const tokenExpirationTime = exp * 1000;
  return tokenExpirationTime < new Date().getTime();
};

// getExpireLength should be of the format
export const getExpireLength = (): string => {
  const lsExp = localStorageHelper.get(AuthStorage.expire_length);
  if (!!lsExp) {
    return lsExp;
  }
  return '4h'; // default to 4h
};

/**
 * Registers a listener for storage events to keep in-memory cache in sync across tabs.
 * This is important for multi-tab scenarios where tokens or user info may change.
 */
export function registerTokenStorageEventListener() {
  return getTokenStorage().registerStorageEventListener();
}
